import React from "react";
import { Box, Typography, IconButton, Tooltip, useTheme } from "@mui/material";
import { Menu } from "../../../components/Common/Menu";
import {
  DotsThree,
  ArrowsOutSimple,
  ArrowsInSimple,
  Trash,
  ArrowsOut,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import { useDarkMode } from "../../../hooks/useDarkMode";
import styles from "./WidgetShell.module.css";

export interface WidgetShellProps {
  title: string;
  spaceName?: string;
  icon?: React.ReactNode;
  onRemove: () => void;
  onToggleExpand: () => void;
  onToggleMinimize: () => void;
  isExpanded?: boolean;
  isMinimized?: boolean;
  children: React.ReactNode;
  rightActions?: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  hideMinimize?: boolean;
  hideExpand?: boolean;
  noPadding?: boolean;
}

const WidgetShell: React.FC<WidgetShellProps> = ({
  title,
  spaceName,
  icon,
  onRemove,
  onToggleExpand,
  onToggleMinimize,
  isExpanded,
  isMinimized,
  children,
  rightActions,
  onMouseEnter,
  onMouseLeave,
  hideMinimize = false,
  hideExpand = false,
  noPadding = false,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const containerRef = React.useRef<HTMLDivElement>(null);
  const menuBtnRef = React.useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const { semanticColors, customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleRemove = () => {
    onRemove();
    handleMenuClose();
  };

  const isMenuOpen = Boolean(menuAnchorEl);
  const isBackgroundWidget = noPadding && !title && !icon;

  const getButtonStyles = () => ({
    width: 28,
    height: 28,
    padding: "4px",
    borderRadius: "4px",
    ...(isBackgroundWidget && {
      backgroundColor: semanticColors.buttonBackground,
      "&:hover": {
        backgroundColor: semanticColors.buttonBackgroundHover,
      },
    }),
  });

  return (
    <Box
      ref={containerRef}
      className={styles.container}
      data-minimized={isMinimized ? "true" : "false"}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        ...(noPadding ? { padding: 0 } : {}),
        backgroundColor: semanticColors.widgetBackground,
        borderColor: semanticColors.widgetBorder,
      }}
    >
      <Box
        className={styles.header}
        sx={{
          display: title || icon ? "flex" : noPadding ? "flex" : "none",
          position: noPadding && !title && !icon ? "absolute" : "relative",
          top: noPadding && !title && !icon ? "8px" : "auto",
          right: noPadding && !title && !icon ? "8px" : "auto",
          zIndex: noPadding && !title && !icon ? 10 : "auto",
          marginBottom: noPadding && !title && !icon ? 0 : undefined,
        }}
      >
        <Box className={styles.titleContainer}>
          {icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#fff",
                flexShrink: 0,
                padding: "6.4px",
                borderRadius: "9999px",
                background: semanticColors.iconBackground,
              }}
            >
              {icon}
            </Box>
          )}
          {title && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                width: "calc(100% - 100px)",
                maxWidth: "280px",
              }}
            >
              <Typography
                variant="h6"
                className={styles.title}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  color: semanticColors.widgetTitle,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: semanticColors.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  textTransform: "capitalize",
                }}
              >
                {spaceName}
              </Typography>
            </Box>
          )}
        </Box>

        <Box className={styles.actionsContainer}>
          {!isMinimized && rightActions}

          {/* Minimize/Maximize Button */}
          {!isExpanded && !hideMinimize && (
            <Tooltip
              title={isMinimized ? "Maximize widget" : "Minimize widget"}
              arrow
              disableInteractive
            >
              <IconButton
                className="widget-minimize-btn"
                size="small"
                onClick={onToggleMinimize}
                onMouseDown={(e) => e.stopPropagation()}
                sx={getButtonStyles()}
              >
                {isMinimized ? (
                  <CaretUp size={16} color={semanticColors.icon} />
                ) : (
                  <CaretDown size={16} color={semanticColors.icon} />
                )}
              </IconButton>
            </Tooltip>
          )}

          {/* Expand/Collapse Button */}
          {!isMinimized && !hideExpand && (
            <Tooltip
              title={isExpanded ? "Collapse widget" : "Expand widget"}
              arrow
              disableInteractive
            >
              <IconButton
                className="widget-expand-btn"
                size="small"
                onClick={onToggleExpand}
                onMouseDown={(e) => e.stopPropagation()}
                sx={getButtonStyles()}
              >
                {isExpanded ? (
                  <ArrowsInSimple size={16} color={semanticColors.icon} />
                ) : (
                  <ArrowsOutSimple size={16} color={semanticColors.icon} />
                )}
              </IconButton>
            </Tooltip>
          )}

          {/* Options Button */}
          <Tooltip title="Widget options" arrow disableInteractive>
            <IconButton
              ref={menuBtnRef}
              className="widget-menu-btn"
              size="small"
              onClick={handleMenuOpen}
              onMouseDown={(e) => e.stopPropagation()}
              aria-haspopup="true"
              aria-expanded={isMenuOpen}
              sx={getButtonStyles()}
            >
              <DotsThree size={16} color={semanticColors.icon} />
            </IconButton>
          </Tooltip>

          {/* Drag Button */}
          <Tooltip title="Move widget" arrow disableInteractive>
            <IconButton
              className="widget-drag-handle"
              aria-label="Move widget"
              size="small"
              sx={{
                ...getButtonStyles(),
                cursor: "grab",
              }}
            >
              <ArrowsOut size={16} color={semanticColors.icon} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        items={[
          {
            label: "Remove",
            icon: <Trash size={20} color="#EF4444" />,
            onClick: handleRemove,
            color: "#EF4444",
            hoverBackgroundColor: isDarkMode ? palette.gray[800] : "#FEF2F2",
          },
        ]}
      />

      {!isMinimized && children}
    </Box>
  );
};

export default WidgetShell;
